/**
 * Titles, descriptions and structured-data builders for every route.
 *
 * Kept beside the other data modules so page copy and business facts stay in
 * one place: the schema builders read companyData/productsData rather than
 * restating an address or a price that could then drift.
 *
 * Titles are written to stay under ~60 characters and descriptions under ~160
 * so neither gets truncated in a result snippet.
 */

import { company } from "./companyData";
import { productsData } from "./productsData";
import { SITE_URL } from "../components/Seo";

export const pageMeta = {
  home: {
    title: "Industrial Chemical Importer in Mumbai | Devanshi Corporation",
    description:
      "Importer and wholesale supplier of industrial chemicals, mineral salts and agricultural humates in Mumbai since 2000. Certified purity, pan-India delivery.",
    path: "/",
  },
  products: {
    title: "Industrial Chemicals Catalogue | Devanshi",
    description: `${productsData.length} industrial and technical grade compounds in stock in Mumbai: carbonates, molybdates, humates, oxides and acids. Purity and packing published, prices on request.`,
    path: "/products",
  },
  about: {
    title: "About Us | Chemical Importer in Mumbai Since 2000",
    description: `${company.name} has supplied industrial chemicals to 500+ clients across agriculture, glass, pharmaceutical and feed industries since ${company.established}. Mumbai-based importer and wholesaler.`,
    path: "/about",
  },
  contact: {
    title: "Contact Us | Industrial Chemical Supplier, Mumbai",
    description: `Call ${company.phones.office.display} or email ${company.email} for a landed price on bulk industrial chemicals. Warehouse and office in Vidyavihar West, Mumbai.`,
    path: "/contact",
  },
  search: {
    title: "Search the Catalogue | Devanshi Corporation",
    description:
      "Search industrial chemicals, mineral salts and agricultural humates by name, formula or application.",
    path: "/search",
    /* A query-string results page is thin, duplicated content: useful to a
       visitor, nothing a crawler should index or spend crawl budget on. */
    robots: "noindex, follow",
  },
  notFound: {
    title: "Page Not Found | Devanshi Corporation",
    description: "The page you asked for is not in the catalogue.",
    path: "/404",
    robots: "noindex, follow",
  },
};

/**
 * "K₂CO₃" -> "K2CO3". The catalogue stores formulae with Unicode subscripts,
 * which look right on the page but are not what anyone types into a search
 * box, so titles and descriptions use the ASCII form.
 */
const SUBSCRIPTS = { "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4", "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9" };

export const asciiFormula = (formula = "") =>
  formula.replace(/[₀-₉]/g, (c) => SUBSCRIPTS[c] || c);

/**
 * Title/description/canonical for a single product detail page.
 *
 * og:image points at the pre-rendered JPEG in /images/og/ rather than the
 * catalogue's own WebP: WhatsApp - the channel most of these links get pasted
 * into - will not render a WebP link preview.
 */
export const productMeta = (product, path) => {
  const formula = asciiFormula(product.formula);
  const uses = (product.applications || []).slice(0, 3).join(", ").toLowerCase();

  return {
    title: `${product.name} (${formula}) Supplier in Mumbai | Devanshi`,
    description: `${product.name} ${product.grade}, ${product.purity} purity, ${product.packaging}. For ${uses}. Bulk supply from Mumbai with certificate of analysis.`,
    path,
    image: `${SITE_URL}/images/og/${product.slug}.jpg`,
    imageAlt: `${product.name} ${product.grade}, ${product.appearance}`,
  };
};

/** Breadcrumb trail, mirroring the visible .crumbs nav on each page. */
export const breadcrumbSchema = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Home", path: "/" }, ...trail].map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const productSchema = (product, path) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${product.name} ${product.grade}`,
    description: product.description,
    image: [
      `${SITE_URL}/images/og/${product.slug}.jpg`,
      `${SITE_URL}${encodeURI(product.image)}`,
    ],
    sku: `DC-${String(product.id).padStart(3, "0")}`,
    category: product.category,
    url: `${SITE_URL}${path}`,
    brand: { "@type": "Brand", name: company.name },
    additionalProperty: [
      { "@type": "PropertyValue", name: "Chemical formula", value: product.formula },
      { "@type": "PropertyValue", name: "Purity", value: product.purity },
      { "@type": "PropertyValue", name: "Appearance", value: product.appearance },
      { "@type": "PropertyValue", name: "Packaging", value: product.packaging },
    ],
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}${path}`,
      priceCurrency: "INR",
      /* Every product is priced on request, so the offer never claims a price. */
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: company.name },
      areaServed: { "@type": "Country", name: "India" },
    },
  };
};

/** The catalogue as an ordered list, so /products can rank as a collection. */
export const catalogueSchema = (products, pathFor) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: `${company.name} industrial chemical catalogue`,
  numberOfItems: products.length,
  itemListElement: products.map((product, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${product.name} ${product.grade}`,
    url: `${SITE_URL}${pathFor(product)}`,
  })),
});
